// Cart stored as JSON in a signed httpOnly cookie. Cart is local UI state;
// the Bokun shopping-cart session is created at checkout time.
import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { z } from "zod";

const COOKIE = "mm_cart";
const MAX_AGE = 60 * 60 * 24 * 7; // 7 days

export const CartItemSchema = z.object({
  productId: z.string().min(1),
  title: z.string().min(1).max(300),
  image: z.string().url().nullable().optional(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  startTimeId: z.number().int().nullable().optional(),
  startTime: z.string().max(20).nullable().optional(),
  currency: z.string().max(8).nullable().optional(),
  pricingCategoryBookings: z
    .array(
      z.object({
        pricingCategoryId: z.string().min(1),
        title: z.string().min(1).max(120),
        quantity: z.number().int().min(0).max(50),
        unitPrice: z.number().min(0).nullable().optional(),
      }),
    )
    .min(1)
    .max(20),
  extras: z
    .array(
      z.object({
        extraId: z.string().min(1),
        title: z.string().min(1).max(160),
        quantity: z.number().int().min(0).max(20),
        unitPrice: z.number().min(0).nullable().optional(),
      }),
    )
    .max(20)
    .default([]),
  pickupPlaceId: z.string().nullable().optional(),
  unitTotal: z.number().min(0).nullable().optional(),
});

export type CartItem = z.infer<typeof CartItemSchema>;

export interface Cart {
  items: (CartItem & { id: string })[];
  updatedAt: string;
}

function readCart(): Cart {
  const raw = getCookie(COOKIE);
  if (!raw) return { items: [], updatedAt: new Date().toISOString() };
  try {
    const parsed = JSON.parse(raw) as Cart;
    if (!Array.isArray(parsed.items)) return { items: [], updatedAt: new Date().toISOString() };
    return parsed;
  } catch {
    return { items: [], updatedAt: new Date().toISOString() };
  }
}

function writeCart(cart: Cart): void {
  setCookie(COOKIE, JSON.stringify(cart), {
    httpOnly: true,
    sameSite: "lax",
    secure: true,
    path: "/",
    maxAge: MAX_AGE,
  });
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export const getCart = createServerFn({ method: "GET" }).handler(async (): Promise<Cart> => {
  return readCart();
});

export const addToCart = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => CartItemSchema.parse(d))
  .handler(async ({ data }): Promise<Cart> => {
    const cart = readCart();
    const item = { ...data, id: newId(), extras: data.extras ?? [] };
    // Compute unitTotal if missing
    if (item.unitTotal == null) {
      const cats = item.pricingCategoryBookings.reduce(
        (s, b) => s + (b.unitPrice ?? 0) * b.quantity,
        0,
      );
      const ex = (item.extras ?? []).reduce((s, b) => s + (b.unitPrice ?? 0) * b.quantity, 0);
      item.unitTotal = cats + ex;
    }
    cart.items.push(item);
    cart.updatedAt = new Date().toISOString();
    writeCart(cart);
    return cart;
  });

export const removeCartItem = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().min(1) }).parse(d))
  .handler(async ({ data }): Promise<Cart> => {
    const cart = readCart();
    cart.items = cart.items.filter((i) => i.id !== data.id);
    cart.updatedAt = new Date().toISOString();
    writeCart(cart);
    return cart;
  });

export const clearCart = createServerFn({ method: "POST" }).handler(async (): Promise<Cart> => {
  const empty: Cart = { items: [], updatedAt: new Date().toISOString() };
  writeCart(empty);
  return empty;
});
